import type { Theme } from "@mui/material/styles";
import { white } from "../../../../../themes/sbux/color-palettes";

const styles = (theme: Theme) => ({
  toolbarContainer: {
    display: "flex",
    alignItems: "center",
    padding: "8px 0",
    minHeight: "48px",
    width: "100%",
  },
  datagridContainer: {
    flex: 1,
    minHeight: "300px",
    width: "100%",
    overflow: "hidden",
    "& .MuiDataGrid-columnHeaderTitleContainerContent": {
      color: white.neutral,
    },
    "& .MuiDataGrid-columnHeader--last .MuiDataGrid-columnSeparator": {
      display: "none",
    },
    "& .MuiDataGrid-row--borderBottom .MuiDataGrid-filler": {
      borderBottom: "none !important",
    },
    cursor: "pointer",
    "& .MuiDataGrid-root": {
      height: "100% !important",
      maxHeight: "100% !important",
      display: "flex",
      flexDirection: "column",
    },
    "& .MuiDataGrid-main": {
      flex: "1 1 auto",
      overflow: "auto",
      minHeight: 0,
    },
    "& .MuiDataGrid-virtualScroller": {
      flex: "1 1 auto",
      overflow: "auto",
    },
    "& .MuiDataGrid-footerContainer": {
      flex: "0 0 auto",
      minHeight: "52px",
    },
  },

  // ── Modal layout ────────────────────────────────────────────────────────────
  modalContent: {
    display: "flex",
    flexDirection: "column" as const,
    gap: theme.spacing(2),
    padding: theme.spacing(1, 0),
  },

  // ── Section wrapper ─────────────────────────────────────────────────────────
  section: {
    display: "flex",
    flexDirection: "column" as const,
    gap: theme.spacing(0.75),
  },

  sectionLabel: {
    fontWeight: 500,
    fontSize: 14,
    color: theme.palette.text.secondary,
  },

  // ── Bot summary grid ────────────────────────────────────────────────────────
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: theme.spacing(0.5, 2),
  },

  summaryItem: {
    display: "flex",
    flexDirection: "column" as const,
  },

  summaryKey: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    fontWeight: 400,
  },

  summaryValue: {
    fontSize: 13,
    wordBreak: "break-all" as const,
  },

  // ── Scrollable utterance/missed list ────────────────────────────────────────
  scrollableList: {
    maxHeight: 180,
    overflowY: "auto" as const,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape?.borderRadius ?? 4,
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
  },

  utteranceLine: {
    fontSize: 13,
    padding: theme.spacing(0.25, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },

  // ── Compact table for slots / slot types ────────────────────────────────────
  compactTableContainer: {
    maxHeight: 180,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape?.borderRadius ?? 4,
    overflow: "auto",
    "& .MuiTableBody-root .MuiTableRow-root:hover": {
      backgroundColor: "inherit",
    },
  },

  slotTypesTableContainer: {
    maxHeight: 180,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape?.borderRadius ?? 4,
    overflow: "auto",
    "& .MuiTableBody-root .MuiTableRow-root:hover": {
      backgroundColor: "rgb(204,204,204,0.5) !important",
      backgroundImage: "linear-gradient(#e8e8e8, #e6e6e6) !important",
    },
  },

  compactTableHead: {
    backgroundColor: theme.palette.action.hover,
  },

  compactTableCell: {
    fontSize: 12,
    padding: theme.spacing(0.5, 1),
  },

  compactTableHeadCell: {
    fontSize: 12,
    fontWeight: 600,
    padding: theme.spacing(0.5, 1),
  },

  // ── Elicitation prompt chip-list ────────────────────────────────────────────
  promptList: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: theme.spacing(0.5),
  },

  promptChip: {
    fontSize: 11,
    height: 22,
  },

  // ── Slot type values ────────────────────────────────────────────────────────
  slotTypeValueEntry: {
    padding: theme.spacing(0.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },

  slotTypeValueName: {
    fontSize: 13,
    fontWeight: 500,
  },

  synonymLine: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    paddingLeft: theme.spacing(2),
  },

  valueTypeBadge: {
    display: "inline-block",
    fontSize: 11,
    fontWeight: 600,
    padding: theme.spacing(0.25, 0.75),
    borderRadius: 100,
    marginBottom: theme.spacing(0.5),
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.text.primary,
  },

  emptyText: {
    fontSize: 12,
    color: theme.palette.text.disabled,
    fontStyle: "italic",
  },

  clickableRow: {
    cursor: "pointer",
  },

  sectionAnnotation: {
    fontSize: 11,
    fontWeight: 400,
    marginLeft: theme.spacing(0.75),
    opacity: 0.7,
  },
});

export default styles;
